import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { formatCurrency } from '@/lib/currency';
import { SidebarNav } from '@/pages/AccountsPage';
import {
  Plus, LogOut, Menu, X, Target, PiggyBank, LifeBuoy,
  AlertCircle, Trash2, TrendingUp, ArrowDown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GoalsPage = () => {
  const { user, logout, isAdmin, currency, locale } = useAuth();
  const navigate = useNavigate();
  const fmt = (v) => formatCurrency(v, currency, locale);

  const [goals, setGoals]   = useState([]);
  const [debts, setDebts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [goalDialog, setGoalDialog] = useState(false);
  const [goalForm, setGoalForm] = useState({
    name: '', type: 'savings', target_amount: '', current_amount: '', deadline: ''
  });
  const [contribDialog, setContribDialog] = useState(null);
  const [contribAmount, setContribAmount] = useState('');

  const [debtDialog, setDebtDialog] = useState(false);
  const [debtForm, setDebtForm] = useState({
    creditor: '', original_amount: '', current_balance: '',
    interest_rate: '', monthly_payment: '', due_day: '', deadline: ''
  });
  const [payDialog, setPayDialog] = useState(null);
  const [payAmount, setPayAmount] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [g, d] = await Promise.all([
        fetch(`${API_URL}/api/goals`, { headers }).then(r => r.ok ? r.json() : []),
        fetch(`${API_URL}/api/debts`, { headers }).then(r => r.ok ? r.json() : []),
      ]);
      setGoals(g); setDebts(d);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleGoalSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    await fetch(`${API_URL}/api/goals`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: goalForm.name,
        type: goalForm.type,
        target_amount: parseFloat(goalForm.target_amount),
        current_amount: goalForm.current_amount ? parseFloat(goalForm.current_amount) : 0,
        deadline: goalForm.deadline || null,
        color: goalForm.type === 'emergency_fund' ? '#F59E0B' : '#10B981',
        icon: goalForm.type === 'emergency_fund' ? 'life-buoy' : 'piggy-bank'
      })
    });
    setGoalDialog(false);
    setGoalForm({ name: '', type: 'savings', target_amount: '', current_amount: '', deadline: '' });
    fetchAll();
  };

  const handleContribute = async (e) => {
    e.preventDefault();
    if (!contribDialog) return;
    const token = localStorage.getItem('token');
    await fetch(`${API_URL}/api/goals/${contribDialog.id}/contribute`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount: parseFloat(contribAmount) })
    });
    setContribDialog(null);
    setContribAmount('');
    fetchAll();
  };

  const deleteGoal = async (g) => {
    if (!window.confirm(`¿Eliminar la meta "${g.name}"?`)) return;
    const token = localStorage.getItem('token');
    await fetch(`${API_URL}/api/goals/${g.id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
    });
    fetchAll();
  };

  const handleDebtSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    const original = parseFloat(debtForm.original_amount);
    await fetch(`${API_URL}/api/debts`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        creditor: debtForm.creditor,
        original_amount: original,
        current_balance: debtForm.current_balance ? parseFloat(debtForm.current_balance) : original,
        interest_rate: debtForm.interest_rate ? parseFloat(debtForm.interest_rate) : 0,
        monthly_payment: debtForm.monthly_payment ? parseFloat(debtForm.monthly_payment) : null,
        due_day: debtForm.due_day ? parseInt(debtForm.due_day, 10) : null,
        deadline: debtForm.deadline || null
      })
    });
    setDebtDialog(false);
    setDebtForm({ creditor: '', original_amount: '', current_balance: '', interest_rate: '', monthly_payment: '', due_day: '', deadline: '' });
    fetchAll();
  };

  const handleDebtPayment = async (e) => {
    e.preventDefault();
    if (!payDialog) return;
    const token = localStorage.getItem('token');
    await fetch(`${API_URL}/api/debts/${payDialog.id}/payment`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount: parseFloat(payAmount) })
    });
    setPayDialog(null);
    setPayAmount('');
    fetchAll();
  };

  const deleteDebt = async (d) => {
    if (!window.confirm(`¿Eliminar la deuda "${d.creditor}"?`)) return;
    const token = localStorage.getItem('token');
    await fetch(`${API_URL}/api/debts/${d.id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
    });
    fetchAll();
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  const emergencyGoal = goals.find(g => g.type === 'emergency_fund');
  const savingsGoals  = goals.filter(g => g.type === 'savings');
  const totalSaved    = goals.reduce((s, g) => s + g.current_amount, 0);
  const totalTarget   = goals.reduce((s, g) => s + g.target_amount, 0);
  const totalDebt     = debts.reduce((s, d) => s + d.current_balance, 0);

  return (
    <div className="min-h-screen bg-zinc-950 flex">
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-label="Menú">
          <div className="fixed inset-0 bg-black/60" onClick={() => setMobileMenuOpen(false)} />
          <aside className="fixed left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-zinc-900 border-r border-zinc-800 p-6 z-50 overflow-y-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-2xl font-black gradient-text">WSFinances</h1>
                <p className="text-zinc-500 text-sm mt-1">Panel de Finanzas</p>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="text-zinc-300 p-2 -mr-2" aria-label="Cerrar menú">
                <X className="h-6 w-6" />
              </button>
            </div>
            <SidebarNav active="goals" isAdmin={isAdmin} onClick={() => setMobileMenuOpen(false)} />
            <div className="mt-8 pt-6 border-t border-zinc-800">
              <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700 mb-4">
                <p className="text-sm text-zinc-400">Conectado como</p>
                <p className="font-medium text-white truncate">{user?.name}</p>
              </div>
              <Button variant="ghost" className="w-full justify-start text-zinc-300 hover:text-red-400" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" /> Cerrar Sesión
              </Button>
            </div>
          </aside>
        </div>
      )}

      <aside className="w-64 sidebar border-r border-zinc-800 p-6 hidden lg:block relative min-h-screen">
        <div className="mb-8">
          <h1 className="text-2xl font-black gradient-text">WSFinances</h1>
          <p className="text-zinc-500 text-sm mt-1">Panel de Finanzas</p>
        </div>
        <SidebarNav active="goals" isAdmin={isAdmin} />
        <div className="absolute bottom-6 left-6 right-6">
          <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700 mb-4">
            <p className="text-sm text-zinc-400">Conectado como</p>
            <p className="font-medium text-white truncate">{user?.name}</p>
          </div>
          <Button variant="ghost" className="w-full justify-start text-zinc-300 hover:text-red-400" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" /> Cerrar Sesión
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 overflow-x-hidden">
        <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <button className="lg:hidden text-zinc-300 p-2 -ml-2" onClick={() => setMobileMenuOpen(true)} aria-label="Abrir menú">
              <Menu className="h-6 w-6" />
            </button>
            <div className="min-w-0">
              <h2 className="text-2xl sm:text-3xl font-bold text-white">Metas y Deudas</h2>
              <p className="text-zinc-400 text-sm">Ahorros, fondo de emergencia y seguimiento de deudas</p>
            </div>
          </div>
        </div>

        {/* Top summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <Card className="bg-zinc-900/60 border-zinc-800">
            <CardContent className="p-5">
              <p className="text-xs text-zinc-400 mb-1">Total ahorrado</p>
              <p className="text-2xl font-bold text-emerald-400">{fmt(totalSaved)}</p>
              {totalTarget > 0 && (
                <p className="text-xs text-zinc-500 mt-1">de {fmt(totalTarget)} ({Math.round(totalSaved / totalTarget * 100)}%)</p>
              )}
            </CardContent>
          </Card>
          <Card className="bg-zinc-900/60 border-zinc-800">
            <CardContent className="p-5">
              <p className="text-xs text-zinc-400 mb-1">Fondo de emergencia</p>
              {emergencyGoal ? (
                <>
                  <p className="text-2xl font-bold text-amber-400">{fmt(emergencyGoal.current_amount)}</p>
                  <p className="text-xs text-zinc-500 mt-1">{emergencyGoal.progress}% de {fmt(emergencyGoal.target_amount)}</p>
                </>
              ) : (
                <p className="text-zinc-500 text-sm">No configurado</p>
              )}
            </CardContent>
          </Card>
          <Card className="bg-zinc-900/60 border-zinc-800">
            <CardContent className="p-5">
              <p className="text-xs text-zinc-400 mb-1">Deuda total</p>
              <p className="text-2xl font-bold text-red-400">{fmt(totalDebt)}</p>
              <p className="text-xs text-zinc-500 mt-1">{debts.length} deuda(s)</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="goals" className="w-full">
          <TabsList className="bg-zinc-900 border border-zinc-800">
            <TabsTrigger value="goals" data-testid="tab-goals">Metas</TabsTrigger>
            <TabsTrigger value="debts" data-testid="tab-debts">Deudas</TabsTrigger>
          </TabsList>

          {/* ------ GOALS TAB ------ */}
          <TabsContent value="goals" className="mt-4">
            <div className="flex justify-end mb-4">
              <Button className="bg-violet-600 hover:bg-violet-700" onClick={() => setGoalDialog(true)} data-testid="new-goal-btn">
                <Plus className="h-4 w-4 mr-2" /> Nueva meta
              </Button>
            </div>

            {loading ? (
              <p className="text-zinc-500">Cargando…</p>
            ) : goals.length === 0 ? (
              <Card className="bg-gradient-to-br from-emerald-500/10 to-violet-500/5 border-emerald-500/30">
                <CardContent className="p-8 text-center">
                  <div className="inline-flex h-14 w-14 rounded-full bg-emerald-500/20 items-center justify-center mb-4">
                    <PiggyBank className="h-7 w-7 text-emerald-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Aún no tienes metas</h3>
                  <p className="text-zinc-400 mb-6">Define tu primer objetivo de ahorro o tu fondo de emergencia.</p>
                  <Button className="bg-violet-600 hover:bg-violet-700" onClick={() => setGoalDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" /> Crear primera meta
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[emergencyGoal, ...savingsGoals].filter(Boolean).map(g => (
                  <Card key={g.id} className="bg-zinc-900/60 border-zinc-800">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="h-11 w-11 rounded-full flex items-center justify-center shrink-0"
                             style={{ backgroundColor: `${g.color}33` }}>
                          {g.type === 'emergency_fund'
                            ? <LifeBuoy className="h-5 w-5" style={{ color: g.color }} />
                            : <PiggyBank className="h-5 w-5" style={{ color: g.color }} />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-white truncate">{g.name}</h3>
                          <p className="text-xs text-zinc-400">
                            {g.type === 'emergency_fund' ? 'Fondo de emergencia' : 'Meta de ahorro'}
                            {g.deadline ? ` · vence ${g.deadline}` : ''}
                          </p>
                        </div>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-400 hover:text-red-400" onClick={() => deleteGoal(g)} aria-label="Eliminar meta">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-zinc-300">{fmt(g.current_amount)}</span>
                        <span className="text-zinc-500">{fmt(g.target_amount)}</span>
                      </div>
                      <div className="h-2 bg-zinc-800 rounded-full overflow-hidden mb-3">
                        <div className="h-full rounded-full" style={{ width: `${Math.min(100, g.progress)}%`, backgroundColor: g.color }} />
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className={`font-medium ${g.progress >= 100 ? 'text-emerald-400' : 'text-zinc-300'}`}>
                          {g.progress}% completado
                        </span>
                        <Button size="sm" variant="outline" className="border-zinc-700 hover:bg-zinc-800 text-zinc-200"
                          onClick={() => setContribDialog(g)} data-testid={`contribute-btn-${g.id}`}>
                          <TrendingUp className="h-3 w-3 mr-1" /> Aportar
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ------ DEBTS TAB ------ */}
          <TabsContent value="debts" className="mt-4">
            <div className="flex justify-end mb-4">
              <Button className="bg-violet-600 hover:bg-violet-700" onClick={() => setDebtDialog(true)} data-testid="new-debt-btn">
                <Plus className="h-4 w-4 mr-2" /> Nueva deuda
              </Button>
            </div>

            {loading ? (
              <p className="text-zinc-500">Cargando…</p>
            ) : debts.length === 0 ? (
              <Card className="bg-gradient-to-br from-red-500/10 to-violet-500/5 border-red-500/30">
                <CardContent className="p-8 text-center">
                  <div className="inline-flex h-14 w-14 rounded-full bg-red-500/20 items-center justify-center mb-4">
                    <AlertCircle className="h-7 w-7 text-red-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Sin deudas registradas</h3>
                  <p className="text-zinc-400 mb-6">Registra préstamos, financiamientos o saldos pendientes para hacer seguimiento.</p>
                  <Button className="bg-violet-600 hover:bg-violet-700" onClick={() => setDebtDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" /> Registrar primera deuda
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {debts.map(d => (
                  <Card key={d.id} className="bg-zinc-900/60 border-zinc-800">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="h-11 w-11 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${d.color}33` }}>
                          <Target className="h-5 w-5" style={{ color: d.color }} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-white truncate">{d.creditor}</h3>
                          <p className="text-xs text-zinc-400">
                            {d.interest_rate > 0 ? `${d.interest_rate}% anual` : 'sin interés'}
                            {d.due_day ? ` · pago día ${d.due_day}` : ''}
                          </p>
                        </div>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-400 hover:text-red-400" onClick={() => deleteDebt(d)} aria-label="Eliminar deuda">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-zinc-300">Pagado: {fmt(d.original_amount - d.current_balance)}</span>
                        <span className="text-zinc-500">de {fmt(d.original_amount)}</span>
                      </div>
                      <div className="h-2 bg-zinc-800 rounded-full overflow-hidden mb-3">
                        <div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.min(100, d.progress)}%` }} />
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-zinc-400">Saldo pendiente</p>
                          <p className="text-lg font-bold text-red-400">{fmt(d.current_balance)}</p>
                        </div>
                        <Button size="sm" variant="outline" className="border-zinc-700 hover:bg-zinc-800 text-zinc-200"
                          onClick={() => setPayDialog(d)} data-testid={`pay-debt-btn-${d.id}`}>
                          <ArrowDown className="h-3 w-3 mr-1" /> Registrar pago
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* New goal dialog */}
        <Dialog open={goalDialog} onOpenChange={setGoalDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-[95vw] sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Nueva meta</DialogTitle>
              <DialogDescription>Crea una meta de ahorro o tu fondo de emergencia</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleGoalSubmit} className="space-y-4">
              <div>
                <Label htmlFor="goal-name">Nombre</Label>
                <Input id="goal-name" required value={goalForm.name}
                  onChange={(e) => setGoalForm({ ...goalForm, name: e.target.value })}
                  className="bg-zinc-800 border-zinc-700" placeholder="Ej: Viaje a Río" />
              </div>
              <div>
                <Label htmlFor="goal-type">Tipo</Label>
                <Select value={goalForm.type} onValueChange={(v) => setGoalForm({ ...goalForm, type: v })}>
                  <SelectTrigger id="goal-type" className="bg-zinc-800 border-zinc-700"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="savings">Meta de ahorro</SelectItem>
                    <SelectItem value="emergency_fund">Fondo de emergencia</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="goal-target">Objetivo</Label>
                  <Input id="goal-target" required inputMode="decimal" type="number" step="0.01"
                    value={goalForm.target_amount}
                    onChange={(e) => setGoalForm({ ...goalForm, target_amount: e.target.value })}
                    className="bg-zinc-800 border-zinc-700" placeholder="5000" />
                </div>
                <div>
                  <Label htmlFor="goal-current">Ya ahorrado</Label>
                  <Input id="goal-current" inputMode="decimal" type="number" step="0.01"
                    value={goalForm.current_amount}
                    onChange={(e) => setGoalForm({ ...goalForm, current_amount: e.target.value })}
                    className="bg-zinc-800 border-zinc-700" placeholder="0" />
                </div>
              </div>
              <div>
                <Label htmlFor="goal-deadline">Fecha límite (opcional)</Label>
                <Input id="goal-deadline" type="date" value={goalForm.deadline}
                  onChange={(e) => setGoalForm({ ...goalForm, deadline: e.target.value })}
                  className="bg-zinc-800 border-zinc-700" />
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-violet-600 hover:bg-violet-700" data-testid="save-goal-btn">Crear meta</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Contribute dialog */}
        <Dialog open={!!contribDialog} onOpenChange={(o) => !o && setContribDialog(null)}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-[95vw] sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Aportar a {contribDialog?.name}</DialogTitle>
              <DialogDescription>
                Saldo actual: {contribDialog ? fmt(contribDialog.current_amount) : ''}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleContribute} className="space-y-4">
              <div>
                <Label htmlFor="contrib-amount">Monto a aportar</Label>
                <Input id="contrib-amount" required inputMode="decimal" type="number" step="0.01"
                  value={contribAmount} onChange={(e) => setContribAmount(e.target.value)}
                  className="bg-zinc-800 border-zinc-700" placeholder="100" autoFocus
                  data-testid="contrib-amount-input" />
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-violet-600 hover:bg-violet-700" data-testid="save-contrib-btn">Aportar</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* New debt dialog */}
        <Dialog open={debtDialog} onOpenChange={setDebtDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-[95vw] sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Nueva deuda</DialogTitle>
              <DialogDescription>Registra una deuda, préstamo o financiamiento</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleDebtSubmit} className="space-y-4">
              <div>
                <Label htmlFor="debt-cred">Acreedor</Label>
                <Input id="debt-cred" required value={debtForm.creditor}
                  onChange={(e) => setDebtForm({ ...debtForm, creditor: e.target.value })}
                  className="bg-zinc-800 border-zinc-700" placeholder="Banco, persona, etc." />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="debt-orig">Monto original</Label>
                  <Input id="debt-orig" required inputMode="decimal" type="number" step="0.01"
                    value={debtForm.original_amount}
                    onChange={(e) => setDebtForm({ ...debtForm, original_amount: e.target.value })}
                    className="bg-zinc-800 border-zinc-700" />
                </div>
                <div>
                  <Label htmlFor="debt-bal">Saldo pendiente</Label>
                  <Input id="debt-bal" inputMode="decimal" type="number" step="0.01"
                    value={debtForm.current_balance}
                    onChange={(e) => setDebtForm({ ...debtForm, current_balance: e.target.value })}
                    className="bg-zinc-800 border-zinc-700" placeholder="= original" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="debt-int">% interés anual</Label>
                  <Input id="debt-int" inputMode="decimal" type="number" step="0.01"
                    value={debtForm.interest_rate}
                    onChange={(e) => setDebtForm({ ...debtForm, interest_rate: e.target.value })}
                    className="bg-zinc-800 border-zinc-700" placeholder="0" />
                </div>
                <div>
                  <Label htmlFor="debt-month">Pago mensual</Label>
                  <Input id="debt-month" inputMode="decimal" type="number" step="0.01"
                    value={debtForm.monthly_payment}
                    onChange={(e) => setDebtForm({ ...debtForm, monthly_payment: e.target.value })}
                    className="bg-zinc-800 border-zinc-700" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="debt-due">Día de pago</Label>
                  <Input id="debt-due" inputMode="numeric" type="number" min="1" max="28"
                    value={debtForm.due_day}
                    onChange={(e) => setDebtForm({ ...debtForm, due_day: e.target.value })}
                    className="bg-zinc-800 border-zinc-700" placeholder="5" />
                </div>
                <div>
                  <Label htmlFor="debt-deadline">Fecha fin (opcional)</Label>
                  <Input id="debt-deadline" type="date"
                    value={debtForm.deadline}
                    onChange={(e) => setDebtForm({ ...debtForm, deadline: e.target.value })}
                    className="bg-zinc-800 border-zinc-700" />
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-violet-600 hover:bg-violet-700" data-testid="save-debt-btn">Crear deuda</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Register debt payment */}
        <Dialog open={!!payDialog} onOpenChange={(o) => !o && setPayDialog(null)}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-[95vw] sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Pagar a {payDialog?.creditor}</DialogTitle>
              <DialogDescription>
                Saldo pendiente: {payDialog ? fmt(payDialog.current_balance) : ''}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleDebtPayment} className="space-y-4">
              <div>
                <Label htmlFor="pay-amount">Monto del pago</Label>
                <Input id="pay-amount" required inputMode="decimal" type="number" step="0.01"
                  value={payAmount} onChange={(e) => setPayAmount(e.target.value)}
                  className="bg-zinc-800 border-zinc-700" autoFocus
                  data-testid="pay-amount-input" />
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-violet-600 hover:bg-violet-700" data-testid="save-pay-btn">Registrar pago</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default GoalsPage;
